<ng-container *transloco="let t; read: 'assignment'">
  <h1 class="mbm">{{ t("title") }}</h1>

  <form
    [formGroup]="filterForm"
    (submit)="onSubmit()"
    (reset)="onReset()"
    class="flex flexDirRow justSpaceBetween twoCol"
  >
    <kendo-formfield>
      <kendo-label [text]="t('keyword')"></kendo-label>
      <kendo-textbox formControlName="keyword"></kendo-textbox>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label [text]="t('contact')"></kendo-label>
      <app-contact-combobox
        formControlName="contact"
        [valuePrimitive]="false"
      ></app-contact-combobox>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label [text]="t('status')"></kendo-label>

      <kendo-multiselect
        [data]="taskStatusesArray"
        textField="name"
        valueField="id"
        [valuePrimitive]="true"
        formControlName="status"
      ></kendo-multiselect>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label text="Auftragsart"></kendo-label>
      <kendo-multiselect
        [data]="assignmentTypes"
        textField="name"
        valueField="id"
        [valuePrimitive]="true"
        formControlName="assignmentTypeIds"
      ></kendo-multiselect>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label [text]="t('execution')"></kendo-label>
      <kendo-multiselect
        [data]="executorOptions"
        textField="name"
        valueField="id"
        [valuePrimitive]="true"
        formControlName="executorIds"
      >
        <ng-template kendoMultiSelectItemTemplate let-dataItem>
          {{ dataItem.firstName }} {{ dataItem.lastName }}
        </ng-template>
        <ng-template kendoMultiSelectTagTemplate let-dataItem>
          {{ dataItem.firstName }} {{ dataItem.lastName }}
        </ng-template>
      </kendo-multiselect>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label [text]="t('hasNoSchedulerItems.label')"></kendo-label>
      <kendo-switch
        formControlName="hasNoSchedulerItems"
        [onLabel]="t('hasNoSchedulerItems.true')"
        [offLabel]="t('hasNoSchedulerItems.false')"
      ></kendo-switch>
    </kendo-formfield>

    <div>
      <button
        kendoButton
        fillMode="solid"
        size="medium"
        rounded="medium"
        themeColor="primary"
        type="submit"
        [disabled]="!filterForm.dirty"
        class="mrm"
        loadingIndication="/Assignment/searchAssignments"
      >
        {{ t("searchButton") }}
      </button>
      <button
        kendoButton
        fillMode="outline"
        size="medium"
        rounded="medium"
        themeColor="primary"
        type="reset"
        *ngIf="showResetButton"
        loadingIndication="/Assignment/searchAssignments"
      >
        {{ t("resetButton") }}
      </button>
    </div>
  </form>

  <div class="saved-filters">
    <div *ngIf="savedFilters.length > 0">
      <span style="margin-right: 0.5rem; font-weight: 500;">{{ t('savedFilters') }}</span>
      <button
        *ngFor="let sf of savedFilters"
        kendoButton
        fillMode="outline"
        size="small"
        rounded="medium"
        themeColor="primary"
        type="button"
        [class.k-hover]="isSavedFilterActive(sf)"
        (click)="applySavedFilter(sf)"
        style="margin-right: 0.3rem;"
      >
        {{ sf.name }}
        <span
          (click)="deleteSavedFilter(sf.id, $event)"
          style="margin-left: 1rem; cursor: pointer; font-weight: bold;"
          [title]="t('delete')"
        >✕</span>
      </button>
    </div>

    <div class="flex store-filter">
      <button
        kendoButton
        fillMode="outline"
        size="medium"
        rounded="medium"
        themeColor="primary"
        type="button"
        [disabled]="isCurrentFilterSaved()"
        (click)="openSaveFilterDialog()"
      >
        {{ t('saveCurrentFilter') }}
      </button>
    </div>
  </div>

  <kendo-dialog
    [title]="t('saveFilterTitle')"
    *ngIf="saveFilterDialogOpened"
    (close)="closeSaveFilterDialog(false)"
    [minWidth]="300"
    [width]="400"
  >
    <div style="padding: 1rem; display: flex; flex-direction: column; gap: 0.5rem;">
      <kendo-label [text]="t('filterName')"></kendo-label>
      <kendo-textbox [(value)]="newFilterName"></kendo-textbox>
    </div>
    <kendo-dialog-actions>
      <button kendoButton (click)="closeSaveFilterDialog(false)">{{ t('cancel') }}</button>
      <button kendoButton themeColor="primary" [disabled]="!newFilterName?.trim()" (click)="closeSaveFilterDialog(true)">{{ t('save') }}</button>
    </kendo-dialog-actions>
  </kendo-dialog>

  <app-delete-confirm-dialog
    [opened]="deleteFilterDialogOpened"
    [message]="t('deleteFilterMessage')"
    (confirmed)="closeDeleteFilterDialog(true)"
    (cancelled)="closeDeleteFilterDialog(false)"
  ></app-delete-confirm-dialog>

  <kendo-grid
    [data]="assignments"
    scrollable="none"
    [skip]="gridState.skip"
    [pageSize]="gridState.pageSize"
    [pageable]="true"
    [resizable]="true"
    (pageChange)="onPageChange($event)"
    [sortable]="{
      allowUnsort: true
    }"
    [sort]="sort"
    (sortChange)="sortChange($event)"
  >
    <ng-template kendoGridToolbarTemplate>
      <button
        *permissionIf="'Permissions.Assignments.Create'"
        kendoButton
        (click)="onCreateNew()"
        fillMode="solid"
        size="medium"
        rounded="medium"
        themeColor="primary"
      >
        {{ t("add") }}
      </button>
      <span
        #columnPickerAnchor
        class="column-picker-anchor"
        style="margin-left: auto; cursor: pointer; display: inline-flex; align-items: center;"
        [title]="t('columnsShowHide')"
        (click)="toggleColumnPicker($event)"
      >{{ t('columns') }}
        <kendo-svg-icon [icon]="columnPickerIcon"></kendo-svg-icon>
      </span>
    </ng-template>

    <ng-container *ngFor="let col of orderedColumns">
      <kendo-grid-column [hidden]="!isColumnVisible(col.field)" media="(min-width: 450px)" [field]="col.field" [title]="getColumnTitle(col.field, t)" [sortable]="col.field !== 'executors'">
        <ng-template kendoGridCellTemplate let-dataItem>
          <ng-container [ngSwitch]="col.field">
            <span *ngSwitchCase="'dueDate'" [ngClass]="dueDateClass(dataItem.dueDate)">
              {{ dataItem.dueDate | date: "dd.MM.yyyy" }}
            </span>
            <ng-container *ngSwitchCase="'createdOn'">
              {{ dataItem.createdOn | date: "dd.MM.yyyy" }}
            </ng-container>
            <ng-container *ngSwitchCase="'client'">
              {{
                dataItem.client.isCompany
                  ? dataItem.client.companyName
                  : dataItem.client.firstName + " " + dataItem.client.lastName
              }}
            </ng-container>
            <ng-container *ngSwitchCase="'owner'">
              {{ (dataItem?.owner?.firstName ?? '') + (dataItem?.owner?.firstName && dataItem?.owner?.lastName ? ' ' : '') + (dataItem?.owner?.lastName ?? '') }}
            </ng-container>
            <ng-container *ngSwitchCase="'assignmentType'">
              {{ dataItem.type?.name }}
            </ng-container>
            <ng-container *ngSwitchCase="'taskStatus'">
              {{ t("taskStatuses." + TaskStatuses[dataItem.taskStatus]) }}
            </ng-container>
            <ng-container *ngSwitchCase="'executors'">
              {{ getExecutorsDisplay(dataItem.executors) }}
            </ng-container>
            <ng-container *ngSwitchCase="'executionLocation'">
              {{ getExecutionLocationDisplay(dataItem.executionLocation) }}
            </ng-container>
            <ng-container *ngSwitchDefault>
              {{ dataItem[col.field] }}
            </ng-container>
          </ng-container>
        </ng-template>
      </kendo-grid-column>
    </ng-container>

    <kendo-grid-column media="(min-width: 450px)" [width]="120">
      <ng-template kendoGridCellTemplate let-dataItem>
        <button
          kendoButton
          [routerLink]="['/assignment', dataItem.id]"
          fillMode="solid"
          size="medium"
          rounded="medium"
          themeColor="primary"
        >
          {{ t("detail") }}
        </button>
      </ng-template>
    </kendo-grid-column>

    <!-- Mobile View -->
  <kendo-grid-column media="(max-width: 450px)">
    <ng-template kendoGridCellTemplate let-dataItem>

        <h3 class="pbs">
          {{dataItem.title}}
        </h3>
      <dl>
        <ng-container *ngFor="let col of orderedColumns">
          <ng-container [ngSwitch]="col.field">

            <ng-container *ngSwitchCase="'title'"><!-- title already shown as h3 --></ng-container>

            <ng-container *ngSwitchCase="'internalNumber'">
              <ng-container *ngIf="isColumnVisible('internalNumber') && dataItem.internalNumber">
                <dt>{{ t('internalNumber') }}</dt>
                <dd>{{dataItem.internalNumber}}</dd>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'client'">
              <ng-container *ngIf="isColumnVisible('client')">
                <dt>{{ t('client') }}</dt>
                <dd>
                  {{ dataItem.client.isCompany
                      ? dataItem.client.companyName ?? ''
                      : ((dataItem.client.firstName ?? '') + ((dataItem.client.firstName && dataItem.client.lastName) ? ' ' : '') + (dataItem.client.lastName ?? ''))
                  }}
                </dd>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'taskStatus'">
              <ng-container *ngIf="isColumnVisible('taskStatus')">
                <dt>{{ t('taskStatus') }}</dt>
                <dd>{{ t("taskStatuses." + TaskStatuses[dataItem.taskStatus]) }}</dd>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'dueDate'">
              <ng-container *ngIf="isColumnVisible('dueDate') && dataItem.dueDate">
                <dt>{{ t('dueDate') }}</dt>
                <dd [ngClass]="dueDateClass(dataItem.dueDate)">{{ dataItem.dueDate | date: "dd.MM.yyyy" }}</dd>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'createdOn'">
              <ng-container *ngIf="isColumnVisible('createdOn')">
                <dt>{{ t('createdOn') }}</dt>
                <dd>{{ dataItem.createdOn | date: "dd.MM.yyyy" }}</dd>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'owner'">
              <ng-container *ngIf="isColumnVisible('owner')">
                <dt>{{ t('owner') }}</dt>
                <dd>{{ (dataItem?.owner?.firstName ?? '') + (dataItem?.owner?.firstName && dataItem?.owner?.lastName ? ' ' : '') + (dataItem?.owner?.lastName ?? '') }}</dd>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'assignmentType'">
              <ng-container *ngIf="isColumnVisible('assignmentType')">
                <dt>{{ t('assignmentType') }}</dt>
                <dd>{{ dataItem.type?.name }}</dd>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'executors'">
              <ng-container *ngIf="isColumnVisible('executors') && dataItem.executors?.length">
                <dt>{{ t('executors') }}</dt>
                <dd>{{ getExecutorsDisplay(dataItem.executors) }}</dd>
              </ng-container>
            </ng-container>

            <ng-container *ngSwitchCase="'executionLocation'">
              <ng-container *ngIf="isColumnVisible('executionLocation') && dataItem.executionLocation">
                <dt>{{ t('executionLocation') }}</dt>
                <dd>{{ getExecutionLocationDisplay(dataItem.executionLocation) }}</dd>
              </ng-container>
            </ng-container>

          </ng-container>
        </ng-container>

        <dd>
          <button
          kendoButton
          [routerLink]="['/assignment', dataItem.id]"
          fillMode="solid"
          size="medium"
          rounded="medium"
          themeColor="primary"
        >
          {{ t("detail") }}
        </button>
        </dd>
      </dl>
    </ng-template>
  </kendo-grid-column>

    <ng-template
      let-totalPages="totalPages"
      let-currentPage="currentPage"
      kendoPagerTemplate
    >
      <div class="flex flexDirRow">
        <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
        <kendo-pager-numeric-buttons
          [buttonCount]="5"
        ></kendo-pager-numeric-buttons>
        <kendo-pager-next-buttons></kendo-pager-next-buttons>
      </div>
      <div>
        <label for="pageBtnGroup" class="mrs">{{
          t("resultsPerPage")
        }}</label>
        <kendo-buttongroup [selection]="'single'" id="pageBtnGroup">
          <button
            (click)="onPageSizeChange(pageSizes.Small)"
            kendoButton
            [selected]="gridState.pageSize === pageSizes.Small"
            [toggleable]="true"
          >
            {{ pageSizes.Small }}
          </button>
          <button
            (click)="onPageSizeChange(pageSizes.Medium)"
            kendoButton
            [selected]="gridState.pageSize === pageSizes.Medium"
            [toggleable]="true"
          >
            {{ pageSizes.Medium }}
          </button>
          <button
            (click)="onPageSizeChange(pageSizes.Large)"
            kendoButton
            [selected]="gridState.pageSize === pageSizes.Large"
            [toggleable]="true"
          >
            {{ pageSizes.Large }}
          </button>
        </kendo-buttongroup>

        <button
          kendoButton
          [title]="t('refresh')"
          (click)="onGridReload()"
          icon="reload"
        ></button>
      </div>
    </ng-template>
    <ng-template kendoGridNoRecordsTemplate>
      <div class="text pl">{{ t("noResults") }}</div>
    </ng-template>
  </kendo-grid>

  <kendo-popup
    *ngIf="columnPickerOpen"
    [anchor]="columnPickerAnchor"
    [anchorAlign]="{ horizontal: 'right', vertical: 'bottom' }"
    [popupAlign]="{ horizontal: 'right', vertical: 'top' }"
  >
    <div
      #columnPickerPopup
      class="column-picker-popup"
      style="padding: 0.5rem; background: white; border: 1px solid #ccc; box-shadow: 0 2px 6px rgba(0,0,0,.15); min-width: 220px;"
    >
      <div
        *ngFor="let col of orderedColumns; let i = index"
        class="column-picker-item"
        [class.drag-over]="dragOverIndex === i"
        [attr.data-col-idx]="i"
      >
        <span
          class="drag-handle"
          (pointerdown)="onPointerDown(i, $event)"
          (pointermove)="onPointerMove($event)"
          (pointerup)="onPointerUp($event)"
          (pointercancel)="onPointerUp($event)"
        >
          <kendo-svg-icon [icon]="dragHandleIcon"></kendo-svg-icon>
        </span>
        <label style="display: flex; align-items: center; cursor: pointer; flex: 1;">
          <input
            type="checkbox"
            kendoCheckBox
            [checked]="isColumnVisible(col.field)"
            (change)="toggleColumn(col.field, $any($event.target).checked)"
            style="margin-right: 0.5rem;"
          />
          {{ col.label }}
        </label>
      </div>
    </div>
  </kendo-popup>
</ng-container>
